<template>
  <BaseModal @close="handleClose" cardWidth="600px" noPadding noToolbar noExit>
    <template #default>
      <div class="w-full flex flex-col">
        <!-- Header -->
        <div class="relative w-full flex items-center gap-2 p-5 border-b border-border-normal">
          <EditTextIcon class="text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Write Custom Description
          </BaseText>
          <button class="absolute top-2 right-2 p-1.5" @click="handleClose">
            <TagRemoveIcon stroke="#5E6678" :width="16" :height="16" />
          </button>
        </div>
        <!-- Content -->
        <div class="w-full flex flex-col p-5 border-b border-border-normal">
          <BaseText type="label" size="sm" class="text-text-muted">
            Description
          </BaseText>
          <!-- Editor -->
          <div class="editor-container">
            <!-- Toolbar -->
            <div class="w-full flex items-center gap-0.5 p-1 border-b border-border-normal">
              <template v-for="action in toolbarActions">
                <div v-if="action.divider" class="w-0 h-4 border-l border-border-normal mx-1" />
                <button v-else class="rounded-md p-1 transition-colors duration-100" 
                :class="action.isActive ? 'bg-neutral-25 text-icon-muted' : 'hover:bg-neutral-25 text-icon-normal'"
                @click="action.action()">
                  <component :is="action.icon" />
                </button>
              </template>
            </div>
            <!-- Text Area -->
            <div class="editor-text-container w-full">
              <div class="content-editor">
                <EditorContent 
                  v-if="editor"
                  :editor="editor"
                />
              </div>
              <!-- Decoration -->
              <div class="editor-decoration pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 12L12 4" stroke="#C1C7D0" stroke-linecap="round"/>
                  <path d="M9 13L13 9" stroke="#C1C7D0" stroke-linecap="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
        <!-- Footer -->
        <div class="w-full flex items-center justify-between p-5">
          <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="handleClose">
            <BaseText type="label" size="sm" class="text-text-muted">
              Cancel
            </BaseText>
          </button>
          <button class="submit-btn px-2.5 py-1.5 rounded-md" :class="submitEnabled ? 'enabled' : 'disabled cursor-default'"
          :disabled="!submitEnabled" @click="handleSubmit">
            <BaseText type="label" size="sm" class="transition-colors duration-100" :class="submitEnabled ? 'text-white' : 'text-text-disabled'">
              Save
            </BaseText>
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'

import EditTextIcon from '../../globals/Icons/EditTextIcon.vue'
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import * as ToolbarIcons from '../../globals/Icons/RichTextToolbar'

export default {
  name: 'EditListingDescriptionModal',
  components: {
    EditorContent,
    EditTextIcon,
    TagRemoveIcon
  },
  props: {
    initialHtmlContent: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Write your description here...'
    }
  },
  data () {
    return {
      editedHtmlContent: this.initialHtmlContent,
      editor: null
    }
  },
  mounted () {
    this.editor = new Editor({
      content: this.initialHtmlContent,
      extensions: [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          strike: false,
          horizontalRule: false,
        }),
        Underline,
        Placeholder.configure({
          placeholder: this.placeholder
        })
      ],
      onUpdate: () => {
        this.editedHtmlContent = this.editor.getHTML()
      }
    })
  },
  beforeDestroy () {
    this.editor.destroy()
  },
  computed: {
    submitEnabled () {
      return this.initialHtmlContent !== this.editedHtmlContent && this.editedHtmlContent.length
    },
    toolbarActions () {
      return [
        {
          key: 'bold',
          icon: ToolbarIcons.BoldTextIcon,
          action: this.toggleBold,
          isActive: this.isActive('bold')
        },
        {
          key: 'italic',
          icon: ToolbarIcons.ItalicTextIcon,
          action: this.toggleItalic,
          isActive: this.isActive('italic')
        },
        {
          key: 'underline',
          icon: ToolbarIcons.UnderlineTextIcon,
          action: this.toggleUnderline,
          isActive: this.isActive('underline')
        },
        { divider: true },
        {
          key: 'bulletList',
          icon: ToolbarIcons.BulletTextIcon,
          action: this.toggleBulletList,
          isActive: this.isActive('bulletList')
        },
        {
          key: 'orderedList',
          icon: ToolbarIcons.NumberTextIcon,
          action: this.toggleOrderedList,
          isActive: this.isActive('orderedList')
        }
      ]
    }
  },
  methods: {
    handleSubmit () {
      this.$emit('save', this.editedHtmlContent.trim())
      this.$emit('close')
    },
    handleClose () {
      if (this.submitEnabled) {
        const confirmed = window.confirm('You have unsaved changes. Are you sure you want to exit the editor?')
        if (!confirmed) return
      }
      this.$emit('close')
    },
    // =========== EDITOR METHODS ===========
    isActive(name, attrs) {
      return this.editor ? this.editor.isActive(name, attrs) : false
    },
    toggleBold() {
      this.editor && this.editor.chain().focus().toggleBold().run()
    },
    toggleItalic() {
      this.editor && this.editor.chain().focus().toggleItalic().run()
    },
    toggleUnderline() {
      this.editor && this.editor.chain().focus().toggleUnderline().run()
    },
    toggleBulletList() {
      this.editor && this.editor.chain().focus().toggleBulletList().run()
    },
    toggleOrderedList() {
      this.editor && this.editor.chain().focus().toggleOrderedList().run()
    }
  }
}
</script>

<style scoped>
.editor-container {
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  box-shadow: 0 1px 2px 0 rgba(0, 56, 108, 0.08), 0 0 0 1px rgba(0, 56, 108, 0.08);
  margin-top: 6px;
}
.editor-text-container {
  position: relative;
  width: 100%;
  height: 262px;
  padding-right: 2px;
  padding-top: 2px;
}
.content-editor {
  height: 258px;
  padding: 8px 18px 0px 12px;
  overflow-y: auto;
}
.content-editor::-webkit-scrollbar {
  width: 5px;
}
.content-editor::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}
.content-editor ::v-deep .ProseMirror {
  outline: none;
  box-shadow: none;
  border: none;

  padding: 0px !important;
  min-height: 250px;

  /* Text Styling */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  color: #060710;
}
.content-editor ::v-deep .ProseMirror.ProseMirror-focused {
  outline: none;
  box-shadow: none;
  border: none;
}
.content-editor ::v-deep .ProseMirror ul,
.content-editor ::v-deep .ProseMirror ol {
  list-style-position: inside;
  margin: 0.25rem 0;
}
.content-editor ::v-deep .ProseMirror ol > li {
  list-style-type: decimal;
}
.content-editor ::v-deep .ProseMirror li > p {
  display: inline;
  margin: 0;
}
.content-editor ::v-deep .ProseMirror p {
  min-height: 20px;
  margin-bottom: 0.5rem;
}

.editor-decoration {
  position: absolute;
  bottom: 8px;
  right: 8px;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}
</style>